import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';

const validKey = key => /^[a-zA-Z0-9_-]{16,100}$/.test(key || '');
export function ticketRequestKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

// Persist the identity before sending: timeouts/reloads must never spend a
// second ticket while the first result is uncertain.
export default function useHalloweenTicket(enabled, user) {
  const storageKey = `slots-halloween-ticket:${user?.tenant_id}:${user?.id}`;
  const [available, setAvailable] = useState(null);
  const [pending, setPending] = useState(null);
  const [message, setMessage] = useState('');
  const lock = useRef(false);
  const scope = useRef(storageKey);
  scope.current = storageKey;
  const refresh = useCallback(async () => {
    const { data } = await api.get('/api/halloween/ticket-state', { timeout: 15000 });
    if (scope.current === storageKey) setAvailable(Number(data.tickets_available || 0));
  }, [storageKey]);
  useEffect(() => {
    if (!enabled || !user?.id) return;
    setAvailable(null); setMessage('');
    try {
      const saved = localStorage.getItem(storageKey);
      setPending(validKey(saved) ? saved : null);
      if (saved && !validKey(saved)) setMessage('Saved ticket request is invalid. Contact support before spinning.');
    } catch { setMessage('Enable site storage to use a ticket safely.'); }
    refresh().catch(() => setMessage('Could not load tickets. Refresh this page to retry.'));
  }, [enabled, user?.id, storageKey, refresh]);
  const request = async (checkOnly = false) => {
    if (lock.current || !enabled || !user?.id) return null;
    lock.current = true;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved && !validKey(saved)) throw new Error('Saved ticket request is invalid. Contact support before spinning.');
      if (checkOnly && !saved) return null;
      const key = saved || ticketRequestKey();
      if (!saved) {
        if (!(available > 0)) throw new Error('No roulette tickets available.');
        localStorage.setItem(storageKey, key);
        if (localStorage.getItem(storageKey) !== key) throw new Error('Enable site storage to use a ticket safely.');
      }
      setPending(key);
      const { data } = checkOnly
        ? await api.get(`/api/halloween/ticket-spins/${key}`, { timeout: 20000 })
        : await api.post('/api/halloween/ticket-spins', { request_key: key }, { timeout: 20000 });
      if (data?.status !== 'completed' || data?.request_key !== key) throw new Error('Could not confirm the ticket result. Retry this same request.');
      if (scope.current !== storageKey) return null;
      localStorage.removeItem(storageKey);
      setPending(null); setMessage(data.message || 'Ticket spin completed.');
      // A replay receipt is historical; get the current balance separately.
      await refresh().catch(() => setAvailable(null));
      return data;
    } catch (error) {
      if (scope.current === storageKey) setMessage(error?.response?.status === 404
        ? 'No completed spin found. Retry the saved request below; it will use the same request ID.'
        : (typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : error.message || 'Result unavailable. Check or retry the same ticket request.'));
      return null;
    } finally { lock.current = false; }
  };
  return { available, pending, message, request };
}
